import React, { useState } from 'react';
import { Music, Check, Home, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const AppleMusicConnectModal = ({ onConnect, onSkip, onClose, isMandatory = false }) => {
  const { isDarkMode } = useTheme();
  const [showOtherServices, setShowOtherServices] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const otherServices = [
    { id: 'spotify', name: 'Spotify', icon: '/brand/streaming/spotify.png' },
    { id: 'soundcloud', name: 'SoundCloud', icon: '/brand/streaming/soundcloud.png' },
    { id: 'tidal', name: 'Tidal', icon: '/brand/streaming/tidal.png' },
    { id: 'pandora', name: 'Pandora', icon: '/brand/streaming/pandora.png' },
  ];

  const handleEmailSubmit = () => {
    if (notifyEmail && selectedService) {
      // TODO: Send email to backend for notification
      console.log('Notify email:', notifyEmail, 'for service:', selectedService);
      setEmailSubmitted(true);
      // Don't auto-close - let user click "Go to Home" button
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      await onConnect();
    } catch (error) {
      console.error('Apple Music connection error:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  // Show other streaming services screen
  if (showOtherServices) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className={`rounded-3xl p-6 max-w-sm w-full text-center relative overflow-hidden ${
          isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'
        }`}>
          {/* Colorful music icon */}
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 via-blue-500 to-teal-400 flex items-center justify-center shadow-lg">
            <Music className="w-8 h-8 text-white" />
          </div>
          
          <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>What streaming do you use?</h3>
          <p className={`text-sm mb-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Get notified when we add your service
          </p>

          {emailSubmitted ? (
            <div className="py-4">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-green-900/30' : 'bg-green-100'
              }`}>
                <Check className={`w-8 h-8 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>You&apos;re on the list!</p>
              <p className={`text-sm mt-1 mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>We&apos;ll notify you when {otherServices.find(s => s.id === selectedService)?.name} is available.</p>
              
              {/* Go to tapinsound.app Button */}
              <a
                href="https://tapinsound.app"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold hover:opacity-90 transition-opacity shadow-lg flex items-center justify-center gap-2 mb-4"
                data-testid="modal-go-home-btn"
              >
                <Home className="w-5 h-5" />
                Go to Tap In
              </a>
              
              {/* Social Media Links - Instagram & LinkedIn @tapinsound */}
              <div className={`pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Follow Tap In for updates</p>
                <div className="flex items-center justify-center gap-4">
                  <a 
                    href="https://instagram.com/tapinsound" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center hover:scale-110 transition-transform"
                    data-testid="social-instagram"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://www.linkedin.com/company/tap-in-sound" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#0A66C2] flex items-center justify-center hover:scale-110 transition-transform"
                    data-testid="social-linkedin"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Service Options with Official Logos */}
              <div className="space-y-2 mb-4">
                {otherServices.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => setSelectedService(service.id)}
                    className={`w-full p-3.5 rounded-2xl border-2 transition-all flex items-center gap-3 ${
                      selectedService === service.id
                        ? isDarkMode 
                          ? 'border-blue-500 bg-blue-900/30' 
                          : 'border-blue-500 bg-blue-50'
                        : isDarkMode
                          ? 'border-gray-700 hover:border-gray-600 bg-gray-800'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`modal-service-${service.id}`}
                  >
                    <img 
                      src={service.icon} 
                      alt={service.name} 
                      className="w-8 h-8 object-contain"
                    />
                    <span className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{service.name}</span>
                    {selectedService === service.id && (
                      <div className="ml-auto w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Email notification input - shows when a service is selected */}
              {selectedService && (
                <div className="mb-4 animate-fade-in">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-2xl placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 text-sm ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-700 text-white' 
                        : 'bg-gray-50 border-gray-200 text-gray-900'
                    }`}
                    data-testid="modal-notify-email"
                  />
                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    We&apos;ll let you know when {otherServices.find(s => s.id === selectedService)?.name} is available
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2.5">
                <button
                  onClick={handleEmailSubmit}
                  disabled={!notifyEmail || !selectedService}
                  className="w-full py-3 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold hover:opacity-90 transition-opacity shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="modal-notify-btn"
                >
                  Notify Me
                </button>
                
                <button
                  onClick={() => setShowOtherServices(false)}
                  className={`w-full py-2.5 font-medium text-sm transition-colors ${
                    isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="modal-back-btn"
                >
                  Back
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Main Apple Music connect screen - mandatory mode has no close button
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`rounded-3xl p-6 max-w-sm w-full text-center relative overflow-hidden ${
        isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'
      }`}>
        {/* Close button - only shown if not mandatory */}
        {!isMandatory && onClose && (
          <button
            onClick={onClose}
            onTouchEnd={(e) => { e.preventDefault(); onClose && onClose(); }}
            className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
              isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-500'
            }`}
            style={{ touchAction: 'manipulation', minWidth: '44px', minHeight: '44px' }}
            data-testid="modal-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        
        {/* Official Apple Music Icon */}
        <div className="w-20 h-20 mx-auto mb-4 rounded-2xl overflow-hidden shadow-lg">
          <img 
            src="/brand/streaming/apple-music.svg" 
            alt="Apple Music" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {isMandatory ? 'Apple Music Required' : 'Connect Apple Music'}
        </h3>
        <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {isMandatory 
            ? 'Connect your Apple Music account to start using Tap In and listen to music with friends.'
            : 'Apple Music is required to use Tap In. Connect now to play full songs and share music with friends.'
          }
        </p>

        {/* Features */}
        <div className="space-y-2 mb-6 text-left">
          {[
            'Play full songs in sessions',
            'Add songs from 100M+ tracks',
            'Sync playback with friends',
          ].map((feature, idx) => (
            <div key={idx} className={`flex items-center gap-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                isDarkMode ? 'bg-green-900/30' : 'bg-green-100'
              }`}>
                <Check className={`w-3 h-3 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              {feature}
            </div>
          ))}
        </div>

        {/* Actions - Following Apple Music brand guidelines */}
        <div className="space-y-3">
          <button
            onClick={handleConnect}
            onTouchEnd={(e) => { e.preventDefault(); handleConnect(); }}
            disabled={isConnecting}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#FA2D48] to-[#FB5C74] text-white font-semibold hover:opacity-90 transition-opacity shadow-lg disabled:opacity-70 flex items-center justify-center gap-2"
            style={{ touchAction: 'manipulation', minHeight: '56px' }}
            data-testid="modal-connect-apple-music"
          >
            {isConnecting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect Apple Music'
            )}
          </button>
          <button
            onClick={() => setShowOtherServices(true)}
            onTouchEnd={(e) => { e.preventDefault(); setShowOtherServices(true); }}
            className={`w-full py-3 rounded-2xl font-medium transition-colors ${
              isDarkMode 
                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            style={{ touchAction: 'manipulation', minHeight: '48px' }}
            data-testid="modal-no-apple-music"
          >
            I don&apos;t have Apple Music
          </button>
        </div>

        {/* Fine print */}
        <p className="text-[10px] text-gray-400 mt-4">
          Requires an active Apple Music subscription. 
          We only access your music library for playback.
        </p>
      </div>
    </div>
  );
};

export default AppleMusicConnectModal;
